// The embed code of the test page: the same `data-*` a partner puts on its
// page, for the three scripts (loader.js the bubble, embed.js the chat in the
// page, search.js the search bar), and the page the preview shows it on.

export type EmbedVariant = "bubble" | "inline" | "search";

/** The modes an embed may open in and offer in its menu. */
export const EMBED_MODES = ["faq", "profession", "discovery", "trainer"] as const;
export type EmbedMode = (typeof EMBED_MODES)[number];

export const EMBED_LANGS = ["de-CH", "fr-CH", "it-CH", "en"] as const;

export interface EmbedSettings {
  variant: EmbedVariant;
  mode: EmbedMode;
  professionId: string;
  professionName: string;
  /** Further modes for the menu; the active mode is always one of them. */
  modes: EmbedMode[];
  lang: string;
  theme: "auto" | "light" | "dark";
  position: "bottom-right" | "bottom-left";
  primaryColor: string;
  brandName: string;
  logoUrl: string;
  greeting: string;
  starters: string[];
  /** A first question the chat sends by itself (bubble and inline). */
  q: string;
  /** What the search bar types as examples, and its prompt. */
  examples: string[];
  placeholder: string;
}

/** The widget's own defaults: a setting at its default stays out of the code. */
export const DEFAULT_EMBED: EmbedSettings = {
  variant: "bubble",
  mode: "faq",
  professionId: "",
  professionName: "",
  modes: [],
  lang: "de-CH",
  theme: "auto",
  position: "bottom-right",
  primaryColor: "#002da5",
  brandName: "",
  logoUrl: "",
  greeting: "",
  starters: [],
  q: "",
  examples: [],
  placeholder: "",
};

const SCRIPT: Record<EmbedVariant, string> = {
  bubble: "loader.js",
  inline: "embed.js",
  search: "search.js",
};

// The container the inline chat and the search bar render into.
const TARGET: Record<EmbedVariant, string | null> = {
  bubble: null,
  inline: "gateway-chat",
  search: "gateway-search",
};

/** Where the scripts come from: the local backend in dev, the LB host in prod. */
export const WIDGET_BASE: string =
  import.meta.env.VITE_WIDGET_BASE_URL ||
  (import.meta.env.PROD ? "https://chat.gateway.one" : "http://127.0.0.1:8080");

const filled = (values: readonly string[]): string[] =>
  values.map((value) => value.trim()).filter(Boolean);

/** The `data-*` attributes in a stable order, each only when it is set. */
export function embedAttributes(s: EmbedSettings): [string, string][] {
  const attrs: [string, string][] = [];
  const add = (name: string, value: string, fallback = "") => {
    const v = value.trim();
    if (v && v !== fallback) attrs.push([name, v]);
  };
  const target = TARGET[s.variant];
  if (target) attrs.push(["data-target", `#${target}`]);
  attrs.push(["data-mode", s.mode]);
  if (s.mode === "profession") {
    add("data-profession-id", s.professionId);
    add("data-profession-name", s.professionName);
  }
  const menu = [...new Set([s.mode, ...s.modes])];
  if (menu.length > 1) attrs.push(["data-modes", menu.join(",")]);
  add("data-lang", s.lang, DEFAULT_EMBED.lang);
  add("data-theme", s.theme, DEFAULT_EMBED.theme);
  if (s.variant === "bubble") add("data-position", s.position, DEFAULT_EMBED.position);
  add("data-primary-color", s.primaryColor.toLowerCase(), DEFAULT_EMBED.primaryColor);
  add("data-brand-name", s.brandName);
  add("data-logo-url", s.logoUrl);
  add("data-greeting", s.greeting);
  const starters = filled(s.starters);
  if (starters.length) attrs.push(["data-starters", JSON.stringify(starters)]);
  if (s.variant === "search") {
    const examples = filled(s.examples);
    if (examples.length) attrs.push(["data-examples", JSON.stringify(examples)]);
    add("data-placeholder", s.placeholder);
  } else {
    add("data-q", s.q);
  }
  return attrs;
}

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  '"': "&quot;",
  "'": "&#39;",
  "<": "&lt;",
  ">": "&gt;",
};

/** Text for an HTML attribute or element: nothing in it can close either. */
export function escapeHtml(value: string): string {
  return value.replace(/[&"'<>]/g, (c) => ESCAPES[c] ?? c);
}

function markup(s: EmbedSettings, base: string, extra: [string, string][]): string {
  const attrs = [...embedAttributes(s), ...extra]
    .map(([name, value]) => `\n  ${name}="${escapeHtml(value)}"`)
    .join("");
  const script = `<script src="${escapeHtml(base)}/widget/${SCRIPT[s.variant]}"${attrs}\n  async defer></script>`;
  const target = TARGET[s.variant];
  if (s.variant === "inline") return `<div id="${target}" style="height: 640px;"></div>\n${script}`;
  if (s.variant === "search") return `<div id="${target}"></div>\n${script}`;
  return script;
}

/** The code to copy onto a partner page. */
export function embedSnippet(s: EmbedSettings, base: string = WIDGET_BASE): string {
  return markup(s, base, []);
}

export interface PreviewPage {
  /** The test page's token; empty when test sessions are off. */
  token: string;
  title: string;
  intro: string;
}

/** A page with the snippet for the preview frame: every load starts fresh
 * (`data-ephemeral`), and the token makes its sessions test sessions. */
export function previewDocument(
  s: EmbedSettings,
  page: PreviewPage,
  base: string = WIDGET_BASE,
): string {
  const extra: [string, string][] = [["data-ephemeral", "true"]];
  if (page.token) extra.push(["data-preview-token", page.token]);
  return [
    "<!doctype html>",
    `<html lang="${escapeHtml(s.lang)}">`,
    "<head>",
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    "<style>body{margin:0;padding:32px;font:16px/1.5 system-ui,sans-serif;color:#1a1a2e;" +
      "background:#f5f6fa}h1{color:#002da5;font-size:24px}</style>",
    "</head>",
    "<body>",
    `<h1>${escapeHtml(page.title)}</h1>`,
    `<p>${escapeHtml(page.intro)}</p>`,
    markup(s, base, extra),
    "</body>",
    "</html>",
  ].join("\n");
}
